import { SORT_OPTIONS } from "@circle-react-shared/MembersList/constants";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { withSearchDelay } from "@circle-react/hooks/utils/withSearchDelay";
import type { CommunityMember } from "@circle-react/types";
import { useAsyncSearch } from "@circleco/compass/hooks/useAsyncSearch";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import {
  buildCommunityMemberOptions,
  type CommunityMemberOption,
} from "./useCommunityMemberSelectOptions";

interface UseCommunityMemberCRMSearchParams {
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  getCommunityMemberText?: (member: CommunityMember) => string;
  includeInactive?: boolean;
  perPage?: number;
  valueField?: "id" | "user_id";
}

// ClickHouse CRM-backed member search.
// Replaces useCommunityMemberSelectAsyncSearch when
// crm_clickhouse_contacts_search_pickers_enabled is on.
// Once the flag is fully rolled out, this becomes the only search hook and
// useCommunityMemberSelectAsyncSearch can be deleted.
export const useCommunityMemberCRMSearch = ({
  excludeAdmins = false,
  filters,
  getCommunityMemberText,
  includeInactive = false,
  perPage = 10,
  valueField = "id",
}: UseCommunityMemberCRMSearchParams) => {
  const queryClient = useQueryClient();
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const [isOpen, setIsOpen] = useState(false);

  const filtersKey = useMemo(
    () => (filters ? JSON.stringify(filters) : null),
    [filters],
  );

  const additionalFilters = useMemo(() => {
    const result = Object.entries(filters ?? {})
      .filter(([key]) => key !== "exclude_ids")
      .map(([key, value]) => ({ key, value }));
    // Exclude community admins server-side (keeps moderators) so the
    // constraint is applied before per_page, not on an already-paginated page.
    if (excludeAdmins) {
      result.push({ key: "role", value: "non_admin" });
    }
    return result;
  }, [filters, excludeAdmins]);

  const excludedIds = useMemo(
    () =>
      new Set(
        (Array.isArray(filters?.exclude_ids) ? filters.exclude_ids : []).map(
          String,
        ),
      ),
    [filters],
  );

  // exclude_ids is keyed by whatever the picker selects on (valueField), and
  // the two id spaces are not interchangeable. valueField "id" callers
  // (add-members, affiliate invite, tags) exclude by community member id;
  // "user_id" callers (post author, event host) exclude by user id, because
  // those forms feed endpoints that resolve a User by id. The backend
  // exclude_contact_ids filter matches on contact id (= member id), so we can
  // only push the exclusion server-side for the "id" path. The "user_id" path
  // stays on the client-side filter below.
  const excludeContactIds = useMemo(
    () =>
      valueField === "id" && Array.isArray(filters?.exclude_ids)
        ? filters.exclude_ids
        : [],
    [filters, valueField],
  );

  const search = useCallback(
    async (query: string, signal: AbortSignal) => {
      const records = await queryClient.fetchQuery({
        queryKey: [
          "community-members",
          "crm-contacts-search",
          query,
          excludeAdmins,
          includeInactive,
          filtersKey,
          perPage,
        ],
        queryFn: async () => {
          const response = await reactQueryGet<{
            records: CommunityMember[];
          }>(
            internalApi.search.contacts.indexV3({
              params: {
                sort: SORT_OPTIONS[0],
                per_page: perPage,
                // Spread-or-nothing, never `key: undefined`. `toQueryString`
                // serializes undefined as the literal string "undefined",
                // which the backend reads as an unknown status and silently
                // drops its confirmed-members-only default.
                ...(includeInactive ? { status: "all" } : {}),
                ...(excludeContactIds.length
                  ? { exclude_contact_ids: excludeContactIds }
                  : {}),
                filters: [
                  {
                    key: "name_and_email",
                    value: query,
                    filter_type: "contains",
                  },
                  { key: "contact_type", value: "community_member" },
                  ...additionalFilters,
                ],
              },
            }),
            { signal },
          );
          return response.records;
        },
        staleTime: 3 * 60 * 1000,
      });

      if (signal.aborted) return [];

      // Source of truth for exclusion, correct for both valueField modes. On
      // the "id" path exclude_contact_ids already dropped these rows before
      // per_page, so this just confirms; on the "user_id" path it is the only
      // exclusion that runs.
      const visibleRecords = records.filter(
        member => !excludedIds.has(String(member[valueField] ?? member.id)),
      );

      return buildCommunityMemberOptions({
        communityMembers: visibleRecords,
        getCommunityMemberText,
        valueField,
        isAdmin,
      });
    },
    [
      queryClient,
      additionalFilters,
      excludeAdmins,
      excludeContactIds,
      excludedIds,
      filtersKey,
      getCommunityMemberText,
      includeInactive,
      isAdmin,
      perPage,
      valueField,
    ],
  );

  const debouncedSearch = useMemo(() => withSearchDelay(search), [search]);

  const onSearch = useCallback(
    (query: string, signal: AbortSignal) =>
      query.trim() ? debouncedSearch(query, signal) : Promise.resolve([]),
    [debouncedSearch],
  );

  const { searchProps } = useAsyncSearch<CommunityMemberOption>({ onSearch });

  const handleInputValueChange: typeof searchProps.onInputValueChange = (
    query,
    details,
  ) => {
    if (
      ["input-change", "input-paste", "input-clear"].includes(details.reason)
    ) {
      setIsOpen(query.trim().length > 0);
    }
    searchProps.onInputValueChange(query, details);
  };

  return {
    isOpen,
    setIsOpen,
    searchProps: { ...searchProps, onInputValueChange: handleInputValueChange },
  };
};
